import { isEnum, isMultienum } from 'customer-data-objects/property/PropertyIdentifier';
import { parseMultiEnumValue } from 'customer-data-property-utils/parseMultiEnumValue';
import { useCallback, useMemo, useState } from 'react';
import { difference, getConditionalPropertyDependencies } from '../../form/utils';
import ConditionalPropertiesPanel from '../../panels/ConditionalPropertiesPanel';
import { getOwnProperty } from '../../utils/getOwnProperty';
import { getConditionalPropertyOptionsRulesWithDependentErrors } from '../conditionalPropertyOptionsRules/getConditionalPropertyOptionsRulesWithDependentErrors';
import { getActiveRules, getIsDependentCpoProperty, getParsedPropertyValueAsArray } from '../conditionalPropertyOptionsRules/utils';
import { useAllPropertyValues } from './useAllPropertyValues';
import { useConditionalPropertyOptionsRulesForObjectType } from './useConditionalPropertyOptionsRulesForObjectType';
import { useConditionalPropertyRulesForObjectType } from './useConditionalPropertyRulesForObjectType';
import { useProperties } from './useProperties';
import emptyFunction from 'react-utils/emptyFunction';
import { jsx as _jsx } from "react/jsx-runtime";
const propertyNameToObject = propertyName => ({
  propertyName
});
const initialState = {
  isOpen: false,
  values: {},
  properties: []
};
const useConditionalPropertiesPanel = ({
  enableConditionalPropertiesPanel = true,
  conditionalPropertyRules,
  onChange,
  onApply = emptyFunction,
  onCancel = emptyFunction,
  property,
  objectTypeId,
  objectId,
  getAdditionalPropertyValue,
  showAutoSaveText,
  value,
  renderInput
}) => {
  const [{
    isOpen,
    values,
    properties
  }, setState] = useState(initialState);

  // PropertyInputV2 will trigger Conditional Properties Panel only for enum properties.
  // As of June 2023, only enum properties can have conditional rules. So, bypassing all panel checks if current property is not enum
  // While expanding conditional rules to other property types, UX should be clarified on handling text properties as
  // panel would need to be triggered without a debounce to avoid multiple onChange calls to host apps
  const eligibleForConditionalLogic = isEnum(property) && enableConditionalPropertiesPanel;
  const {
    data: conditionalPropertyRulesForObjectType,
    loading: conditionalPropertyRulesLoading,
    error: conditionalPropertyRulesError
  } = useConditionalPropertyRulesForObjectType(objectTypeId, {
    skip:
    // don't fetch conditional rules if given via args
    conditionalPropertyRules !== undefined || !eligibleForConditionalLogic
  });
  const {
    data: conditionalPropertyOptionsRules,
    loading: conditionalPropertyOptionsRulesLoading,
    error: conditionalPropertyOptionsRulesError
  } = useConditionalPropertyOptionsRulesForObjectType(objectTypeId, {
    skip: !eligibleForConditionalLogic
  });
  const isDependentCpoProperty = getIsDependentCpoProperty(conditionalPropertyOptionsRules || [], property);
  const {
    data: allPropertiesForObjectType,
    loading: allPropertiesLoading,
    error: allPropertiesError
  } = useProperties(objectTypeId, {
    skip: !isDependentCpoProperty
  });
  const {
    data: allPropertyValues,
    loading: allPropertyValuesLoading,
    error: allPropertyValuesError
  } = useAllPropertyValues({
    objectId,
    objectTypeId,
    skip: !isDependentCpoProperty
  });
  const conditionalPropertyOptionsRulesForProperty = useMemo(() => {
    return (conditionalPropertyOptionsRules || []).filter(rule => rule.controllingField.fieldName === property.name);
  }, [conditionalPropertyOptionsRules, property.name]);
  const closePanel = useCallback(() => {
    setState({
      isOpen: false,
      values: {},
      properties: []
    });
  }, []);
  const openPanel = useCallback((_values, _properties) => {
    setState({
      isOpen: true,
      values: _values,
      properties: _properties
    });
  }, []);
  const maybeOpenConditionalPropertiesPanel = useCallback(changes => {
    // for purposes of calculating what CP rules this edit triggered:
    // 1. only consider values changed as part of this edit
    // 2. when editing a multi-enum, only consider newly-added values.
    //    (otherwise, existing values with CP rules would trigger the CP panel
    //    to appear repeatedly when other values are added/removed)
    const getFormValue = propertyName => {
      const nextValue = getOwnProperty(changes, propertyName);
      if (propertyName !== property.name || !isMultienum(property) || !nextValue || !value) {
        return nextValue;
      }
      const prevValues = new Set(parseMultiEnumValue(value));
      const nextValues = new Set(parseMultiEnumValue(nextValue));
      const added = difference(nextValues, prevValues);
      // callback runs each time an option is selected, so we would expect at
      // most one new value added at a time. on deselection, the `added` array
      // will be empty and we don't have to show CPP
      return added[0];
    };
    const getFormValueWithAdditionalValues = propertyName => {
      if (Object.prototype.hasOwnProperty.call(changes, propertyName)) {
        return changes[propertyName];
      }
      const additionalPropertyValue = getAdditionalPropertyValue(propertyName, objectId, objectTypeId);
      if (additionalPropertyValue !== undefined) {
        return additionalPropertyValue;
      }
      return allPropertyValues ? getOwnProperty(allPropertyValues, propertyName) : undefined;
    };
    const _conditionalPropertyRules = conditionalPropertyRules || conditionalPropertyRulesForObjectType || [];
    const conditionalPropertyDependencies = getConditionalPropertyDependencies(objectTypeId, getFormValue, {
      [property.name]: property
    }, _conditionalPropertyRules);
    const activeConditionalPropertyOptionsRules = getActiveRules(getParsedPropertyValueAsArray(property, changes[property.name]), conditionalPropertyOptionsRulesForProperty);
    const dependentFieldValues = activeConditionalPropertyOptionsRules.reduce((acc, rule) => {
      acc[rule.dependentFieldName] = getFormValueWithAdditionalValues(rule.dependentFieldName);
      return acc;
    }, {});
    const activeConditionalPropertyOptionsRulesWithErrors = getConditionalPropertyOptionsRulesWithDependentErrors(activeConditionalPropertyOptionsRules, allPropertiesForObjectType || {}, dependentFieldValues);
    const activeControllingProperties = Object.keys(conditionalPropertyDependencies);
    const shouldOpenPanel = activeControllingProperties.length > 0 || activeConditionalPropertyOptionsRulesWithErrors.length > 0;
    if (shouldOpenPanel) {
      // if there are no CP dependents, we just show panel with root input
      // and form takes care of adding CPO dependents to the tree
      const initialProperties = activeControllingProperties.length > 0 ? activeControllingProperties.map(propertyNameToObject) : [{
        propertyName: property.name
      }];
      openPanel(changes, initialProperties);
    } else {
      onChange(changes);
    }
  }, [objectTypeId, property, conditionalPropertyRules, conditionalPropertyRulesForObjectType, conditionalPropertyOptionsRulesForProperty, allPropertiesForObjectType, value, getAdditionalPropertyValue, objectId, allPropertyValues, openPanel, onChange]);
  const memoizedDefault = useMemo(() => {
    return {
      maybeOpenConditionalPropertiesPanel: onChange,
      conditionalPropertiesPanel: null,
      loading: false,
      error: undefined
    };
  }, [onChange]);
  if (!eligibleForConditionalLogic) {
    return memoizedDefault;
  }
  const conditionalPropertiesPanel = isOpen ? /*#__PURE__*/_jsx(ConditionalPropertiesPanel, {
    properties: properties,
    onApply: changes => {
      onChange(changes);
      onApply();
      closePanel();
    },
    onCancel: () => {
      onCancel();
      closePanel();
    },
    objectTypeId: objectTypeId,
    objectId: objectId,
    getAdditionalPropertyValue: getAdditionalPropertyValue,
    triggerPropertyDefinition: property,
    defaultValues: values,
    showAutoSaveText: showAutoSaveText,
    renderInput: renderInput
  }) : null;
  return {
    conditionalPropertiesPanel,
    maybeOpenConditionalPropertiesPanel,
    loading: conditionalPropertyRulesLoading || conditionalPropertyOptionsRulesLoading || allPropertiesLoading || allPropertyValuesLoading,
    error: conditionalPropertyRulesError || conditionalPropertyOptionsRulesError || allPropertiesError || allPropertyValuesError
  };
};
export default useConditionalPropertiesPanel;